import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader, Panel, Toolbar, KeyValue, KeyValueList } from '../components/Page';
import { EmptyState, ErrorState, InlineError, ProgressBar, TableSkeleton } from '../components/Feedback';
import { Chip, ConfidenceBadge, IntakeChip } from '../components/Chips';
import { SelectField, TextAreaField, TextField, parseEnumValue } from '../components/Form';
import { TableWrap, SelectableRow } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { submissionBlockedReason } from '../components/Onboarding';
import { AccountLookupField } from '../components/AccountLookup';
import { TermLookupField } from '../components/TermLookup';
import { DocumentPreview } from '../components/DocumentPreview';
import { Drawer } from '../components/Overlay';
import { useAuth } from '../lib/auth';
import { documents, learningAccounts, vendorInvoiceIntake } from '../lib/endpoints';
import { useAsync, useDebounced } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useOnboarding } from '../lib/onboarding';
import { useToast } from '../lib/toast';
import { errorMessage, ApiError } from '../lib/api';
import {
  GatewayScenario,
  GatewayScenarioDescriptions,
  GatewayScenarioLabels,
  IntakeLineReviewState,
  IntakeLineReviewStateLabels,
  IntakeLineReviewStateTones,
  IntakeLineTypeLabels,
  LineRecommendationStatus,
  LineTaxResolution,
  LineTaxResolutionLabels,
  LineTaxResolutionTones,
  LineRecommendationStatusLabels,
  TaxReviewStatus,
  TaxReviewStatusLabels,
  TaxReviewStatusTones,
  VendorInvoiceIntakeStage,
  VendorInvoiceIntakeStageFilters,
  VendorInvoiceIntakeStageLabels,
  VendorInvoiceIntakeStageTones,
} from '../lib/enums';
import type {
  IntakeLineBatchEntry,
  LocalAccountDto,
  QboTaxCodeDto,
  QboTermDto,
  VendorInvoiceIntakeDetailDto,
  VendorInvoiceIntakeLineDto,
  VendorInvoiceIntakeSummaryDto,
} from '../lib/types';
import { formatBytes, formatDate, formatDateTime, formatMoney, formatRelative } from '../lib/format';

/** Mirrors GatewayOptions.MaxUploadMegabytes (default 25) so oversized files fail fast. */
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Scenarios that belong on this queue. New-vendor cards have their own workflow (Vendor Intake)
 *  because they need a match-and-approve step this uploader does not have. */
const INTAKE_UPLOAD_SCENARIOS = [
  GatewayScenario.NewBill,
  GatewayScenario.ExistingBill,
  GatewayScenario.Expense,
  GatewayScenario.PurchaseOrderMatch,
];

type QueueColumn = 'stage' | 'supplier' | 'invoice' | 'total' | 'review' | 'tax' | 'arrived';

function queueSortValue(row: VendorInvoiceIntakeSummaryDto, key: QueueColumn): string | number {
  switch (key) {
    case 'stage':
      return VendorInvoiceIntakeStageLabels[row.stage];
    case 'supplier':
      return row.vendorName ?? '';
    case 'invoice':
      return row.invoiceNumber ?? '';
    case 'total':
      return row.total ?? 0;
    case 'review':
      return row.linesAwaitingReview;
    case 'tax':
      return TaxReviewStatusLabels[row.taxReviewStatus];
    case 'arrived':
      return row.createdUtc;
    default:
      return '';
  }
}

interface UploadItem {
  key: string;
  fileName: string;
  sizeBytes: number;
  progress: number;
  state: 'uploading' | 'done' | 'error';
  message?: string;
  documentId?: string;
  abort?: () => void;
}

/**
 * THE VENDOR INVOICE INTAKE QUEUE.
 *
 * A named queue rather than a filter on Documents, because "the supplier invoices waiting on a
 * person" is a thing the people doing this work talk about and look for. Underneath it is the same
 * document, the same stored file and the same posting engine: this screen adds review, it does not
 * add a second way to post. Posting stays on the invoice review pane, which is where the QuickBooks
 * payload and the approval decision live.
 *
 * Two properties of this screen are deliberate and worth not undoing:
 *
 *   * every save names the revision it was built from, so two reviewers on one invoice cannot
 *     silently overwrite each other - a stale save is refused and says so;
 *   * a line edit is submitted WHOLE, because a line override replaces the stored override for that
 *     line number rather than patching it, and sending only the tax code would clear the account.
 */
export function VendorInvoiceIntakePage(): JSX.Element {
  const navigate = useNavigate();
  const { can } = useAuth();
  const toast = useToast();
  const canReview = can('documents.edit');
  const canUpload = can('documents.upload');

  // Shared onboarding cache (the Dashboard and Documents screen read the same copy). While it
  // loads, uploads stay enabled — the API is the real gate, this only explains a refusal up front.
  const onboarding = useOnboarding();
  const blockedReason = onboarding.data ? submissionBlockedReason(onboarding.data) : null;
  const uploadsBlocked = blockedReason !== null;
  const canDrop = canUpload && !uploadsBlocked;

  const [uploadScenario, setUploadScenario] = useState<GatewayScenario>(GatewayScenario.NewBill);
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [params, setParams] = useSearchParams();
  const selectedId = params.get('document');

  // Lives on the page, not the drawer content, because the drawer's own WIDTH depends on it: with
  // the preview open, the drawer widens to the full screen so the PDF and the bill actions split
  // it in half instead of squeezing both into the drawer's usual two-thirds.
  const [showPreview, setShowPreview] = useState(false);

  const [stage, setStage] = useState<VendorInvoiceIntakeStage | null>(
    VendorInvoiceIntakeStage.NeedsReview,
  );
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounced(search, 350);

  const list = useAsync(
    (signal) =>
      vendorInvoiceIntake.list({ stage, search: debouncedSearch, page, pageSize }, signal),
    [stage, debouncedSearch, page, pageSize],
  );

  const queueTable = useSortableTable<QueueColumn>({
    storageKey: 'vendorInvoiceIntakeQueueColumnWidths',
    columns: {
      stage: { minWidth: 140 },
      supplier: { minWidth: 160 },
      invoice: { minWidth: 130 },
      total: { minWidth: 90 },
      review: { minWidth: 160 },
      tax: { minWidth: 110 },
      arrived: { minWidth: 100 },
    },
  });

  const detail = useAsync(
    (signal) =>
      selectedId ? vendorInvoiceIntake.get(selectedId, signal) : Promise.resolve(null),
    [selectedId],
  );

  const taxCodes = useAsync((signal) => vendorInvoiceIntake.taxCodes(signal), []);
  const termsState = useAsync((signal) => vendorInvoiceIntake.terms(signal), []);
  const accountsState = useAsync((signal) => learningAccounts.allEligible(signal), []);

  const select = useCallback(
    (documentId: string | null) => {
      const next = new URLSearchParams(params);
      if (documentId) next.set('document', documentId);
      else next.delete('document');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const refreshBoth = useCallback(() => {
    detail.reload();
    list.reload();
  }, [detail, list]);

  const startUpload = useCallback(
    (file: File) => {
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      if (file.size > MAX_UPLOAD_BYTES) {
        setUploads((current) => [
          ...current,
          {
            key,
            fileName: file.name,
            sizeBytes: file.size,
            progress: 0,
            state: 'error',
            message: `Too large — the gateway accepts files up to ${formatBytes(MAX_UPLOAD_BYTES)}.`,
          },
        ]);
        return;
      }

      const handle = documents.upload(
        file,
        { scenario: uploadScenario, qboVendorId: null, targetQboEntityId: null, notes: null },
        (percent) => {
          setUploads((current) =>
            current.map((item) => (item.key === key ? { ...item, progress: percent } : item)),
          );
        },
      );

      setUploads((current) => [
        ...current,
        { key, fileName: file.name, sizeBytes: file.size, progress: 0, state: 'uploading', abort: handle.abort },
      ]);

      handle.promise
        .then((summary) => {
          setUploads((current) =>
            current.map((item) =>
              item.key === key
                ? { ...item, progress: 100, state: 'done', documentId: summary?.id, message: summary?.statusMessage ?? undefined }
                : item,
            ),
          );
          toast.success('Upload accepted', `${file.name} is queued for extraction.`);
          list.reload();
        })
        .catch((error: unknown) => {
          const message = errorMessage(error);
          setUploads((current) =>
            current.map((item) => (item.key === key ? { ...item, state: 'error', message } : item)),
          );
          toast.error('Upload failed', `${file.name}: ${message}`);
        });
    },
    [uploadScenario, toast, list],
  );

  const handleUploadFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      if (uploadsBlocked) return;
      Array.from(fileList).forEach(startUpload);
    },
    [startUpload, uploadsBlocked],
  );

  const onUploadDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (!canDrop) return;
    handleUploadFiles(event.dataTransfer.files);
  };

  const onUploadDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current += 1;
    if (canDrop) setDragging(true);
  };

  const onUploadDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = canDrop ? 'copy' : 'none';
  };

  const onUploadDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const activeUploads = uploads.filter((item) => item.state === 'uploading').length;

  // Prev/next move within the current page of the queue only — jumping across a page boundary
  // would mean silently changing the page filter under someone mid-review, which is worse than a
  // Next button that goes quiet at the edge of what is loaded.
  const rows = list.data?.items ?? [];
  const currentIndex = selectedId ? rows.findIndex((row) => row.documentId === selectedId) : -1;
  const prevId =
    currentIndex > 0
      ? (rows.slice(0, currentIndex).reverse().find((row) => row.hasExtraction)?.documentId ?? null)
      : null;
  const nextId =
    currentIndex >= 0
      ? (rows.slice(currentIndex + 1).find((row) => row.hasExtraction)?.documentId ?? null)
      : null;

  return (
    <>
      <PageHeader
        title="Vendor Invoice Intake"
        description="Supplier invoices from arrival to their QuickBooks outcome, with the review each one still needs."
        actions={
          canReview ? (
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={async () => {
                  try {
                    const result = await vendorInvoiceIntake.pullTerms();
                    toast.success(result.message ?? 'Terms read from QuickBooks.');
                    termsState.reload();
                  } catch (cause) {
                    toast.error(errorMessage(cause));
                  }
                }}
              >
                Pull terms
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={async () => {
                  try {
                    const result = await vendorInvoiceIntake.pullTaxCodes();
                    toast.success(result.message ?? 'Tax codes read from QuickBooks.');
                    taxCodes.reload();
                    refreshBoth();
                  } catch (cause) {
                    toast.error(errorMessage(cause));
                  }
                }}
              >
                Pull tax codes
              </button>
            </>
          ) : undefined
        }
      />

      {canUpload ? (
        <Panel
          title="Upload"
          description="Drop a supplier invoice straight into this queue — no trip through Documents needed."
        >
          {uploadsBlocked ? (
            <div
              className={`banner ${onboarding.data?.licence.quotaExhausted ? 'banner-info' : 'banner-warn'} banner-inline`}
              role="status"
            >
              <div className="banner-body">
                <p className="banner-title">Uploads are paused</p>
                <p className="banner-detail">{blockedReason}</p>
              </div>
              <Link className="btn btn-secondary btn-sm" to="/">
                Open the dashboard
              </Link>
            </div>
          ) : null}

          <div className="upload-grid">
            <div className="upload-config">
              <SelectField
                label="Scenario"
                value={String(uploadScenario)}
                onChange={(value) =>
                  setUploadScenario(parseEnumValue<GatewayScenario>(value) ?? GatewayScenario.NewBill)
                }
                options={INTAKE_UPLOAD_SCENARIOS.map((value) => ({
                  value: String(value),
                  label: GatewayScenarioLabels[value],
                }))}
                hint={GatewayScenarioDescriptions[uploadScenario]}
              />

              <div className="notice notice-info">
                <p>Setting up a new supplier? That has its own workflow, so you can check who they
                  are before a vendor card is created.</p>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/vendor-intake')}>
                  Go to Vendor Intake
                </button>
              </div>
            </div>

            <div
              className={['dropzone', dragging ? 'dropzone-active' : '', uploadsBlocked ? 'dropzone-disabled' : '']
                .filter(Boolean)
                .join(' ')}
              aria-disabled={uploadsBlocked || undefined}
              onDrop={onUploadDrop}
              onDragEnter={onUploadDragEnter}
              onDragOver={onUploadDragOver}
              onDragLeave={onUploadDragLeave}
            >
              <div className="dropzone-inner">
                <span className="dropzone-icon" aria-hidden="true">
                  {uploadsBlocked ? '⏸' : '⬇'}
                </span>
                <p className="dropzone-title">
                  {uploadsBlocked ? 'Uploads unavailable' : dragging ? 'Release to upload' : 'Drag an invoice here'}
                </p>
                <p className="dropzone-sub">
                  {uploadsBlocked ? (
                    blockedReason
                  ) : (
                    <>
                      PDF, PNG, JPG or TIFF · up to {formatBytes(MAX_UPLOAD_BYTES)} each · {GatewayScenarioLabels[uploadScenario]}
                    </>
                  )}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={uploadsBlocked}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="visually-hidden"
                  multiple
                  disabled={uploadsBlocked}
                  accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,application/pdf,image/*"
                  aria-label="Choose files to upload"
                  onChange={(event) => {
                    handleUploadFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
              </div>
            </div>
          </div>

          {uploads.length > 0 ? (
            <div className="upload-list" aria-live="polite">
              <div className="upload-list-head">
                <h3 className="upload-list-title">
                  Uploads {activeUploads > 0 ? `(${activeUploads} in progress)` : ''}
                </h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setUploads((current) => current.filter((item) => item.state === 'uploading'))}
                >
                  Clear finished
                </button>
              </div>
              <ul className="upload-items">
                {uploads.map((item) => (
                  <li className={`upload-item upload-${item.state}`} key={item.key}>
                    <div className="upload-item-head">
                      <span className="upload-name">{item.fileName}</span>
                      <span className="upload-size">{formatBytes(item.sizeBytes)}</span>
                      {item.state === 'uploading' ? (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => item.abort?.()}>
                          Cancel
                        </button>
                      ) : item.documentId ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => select(item.documentId!)}
                        >
                          Review here
                        </button>
                      ) : null}
                    </div>
                    <ProgressBar
                      value={item.state === 'done' ? 100 : item.progress}
                      tone={item.state === 'error' ? 'bad' : item.state === 'done' ? 'good' : 'info'}
                      label={`${item.fileName} upload progress`}
                    />
                    {item.message ? <p className="upload-message">{item.message}</p> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Panel>
      ) : null}

      <Panel>
        <Toolbar>
          <SelectField
            label="Stage"
            value={stage === null ? '' : String(stage)}
            onChange={(value) => {
              setStage(value === '' ? null : parseEnumValue<VendorInvoiceIntakeStage>(value));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Every stage' },
              ...VendorInvoiceIntakeStageFilters.map((value) => ({
                value: String(value),
                label: VendorInvoiceIntakeStageLabels[value],
              })),
            ]}
          />
          <TextField
            label="Search"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="File, supplier, invoice number"
          />
        </Toolbar>

        {list.error ? (
          <ErrorState message={list.error} onRetry={list.reload} />
        ) : list.loading && !list.data ? (
          <TableSkeleton columns={7} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title="Nothing here"
            description={
              stage === null
                ? 'No supplier invoices have reached the gateway yet.'
                : `No invoices are at the ${VendorInvoiceIntakeStageLabels[stage]} stage.`
            }
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={queueTable.columnStyle('stage')} />
                <col style={queueTable.columnStyle('supplier')} />
                <col style={queueTable.columnStyle('invoice')} />
                <col style={queueTable.columnStyle('total')} />
                <col style={queueTable.columnStyle('review')} />
                <col style={queueTable.columnStyle('tax')} />
                <col style={queueTable.columnStyle('arrived')} />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Stage"
                    sortKey="stage"
                    currentSortKey={queueTable.sortKey}
                    sortDir={queueTable.sortDir}
                    onSort={queueTable.toggleSort}
                    style={queueTable.columnStyle('stage')}
                    onResizeStart={queueTable.beginResize('stage')}
                  />
                  <SortableTh
                    label="Supplier"
                    sortKey="supplier"
                    currentSortKey={queueTable.sortKey}
                    sortDir={queueTable.sortDir}
                    onSort={queueTable.toggleSort}
                    style={queueTable.columnStyle('supplier')}
                    onResizeStart={queueTable.beginResize('supplier')}
                  />
                  <SortableTh
                    label="Invoice"
                    sortKey="invoice"
                    currentSortKey={queueTable.sortKey}
                    sortDir={queueTable.sortDir}
                    onSort={queueTable.toggleSort}
                    style={queueTable.columnStyle('invoice')}
                    onResizeStart={queueTable.beginResize('invoice')}
                  />
                  <SortableTh
                    label="Total"
                    sortKey="total"
                    currentSortKey={queueTable.sortKey}
                    sortDir={queueTable.sortDir}
                    onSort={queueTable.toggleSort}
                    className="num"
                    style={queueTable.columnStyle('total')}
                    onResizeStart={queueTable.beginResize('total')}
                  />
                  <SortableTh
                    label="Review"
                    sortKey="review"
                    currentSortKey={queueTable.sortKey}
                    sortDir={queueTable.sortDir}
                    onSort={queueTable.toggleSort}
                    style={queueTable.columnStyle('review')}
                    onResizeStart={queueTable.beginResize('review')}
                  />
                  <SortableTh
                    label="Tax"
                    sortKey="tax"
                    currentSortKey={queueTable.sortKey}
                    sortDir={queueTable.sortDir}
                    onSort={queueTable.toggleSort}
                    style={queueTable.columnStyle('tax')}
                    onResizeStart={queueTable.beginResize('tax')}
                  />
                  <SortableTh
                    label="Arrived"
                    sortKey="arrived"
                    currentSortKey={queueTable.sortKey}
                    sortDir={queueTable.sortDir}
                    onSort={queueTable.toggleSort}
                    style={queueTable.columnStyle('arrived')}
                    onResizeStart={queueTable.beginResize('arrived')}
                  />
                </tr>
              </thead>
              <tbody>
                {queueTable.sort(list.data.items, queueSortValue).map((row) => (
                  <QueueRow
                    key={row.documentId}
                    row={row}
                    selected={row.documentId === selectedId}
                    onSelect={() => select(row.documentId)}
                  />
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size: number) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>

      <Drawer
        open={selectedId !== null}
        onClose={() => select(null)}
        width={showPreview ? 'full' : 'xwide'}
        title={detail.data?.summary.vendorName ?? detail.data?.summary.fileName ?? 'Invoice'}
        description={detail.data?.summary.stageReason}
      >
        {detail.error ? (
          <ErrorState message={detail.error} onRetry={detail.reload} />
        ) : detail.loading && !detail.data ? (
          <TableSkeleton columns={6} />
        ) : detail.data ? (
          <IntakeDetail
            detail={detail.data}
            taxCodes={taxCodes.data ?? []}
            terms={termsState.data ?? []}
            termsLoading={termsState.loading}
            accounts={accountsState.data ?? []}
            accountsLoading={accountsState.loading}
            canReview={canReview}
            onChanged={refreshBoth}
            onClose={() => select(null)}
            onPrev={prevId ? () => select(prevId) : null}
            onNext={nextId ? () => select(nextId) : null}
            showPreview={showPreview}
            onTogglePreview={() => setShowPreview((current) => !current)}
          />
        ) : null}
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------------------
// The queue row
// ---------------------------------------------------------------------------

function QueueRow({
  row,
  selected,
  onSelect,
}: {
  row: VendorInvoiceIntakeSummaryDto;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <SelectableRow selected={selected} onSelect={onSelect} label={row.fileName}>
      <td>
        <Chip tone={VendorInvoiceIntakeStageTones[row.stage]} title={row.stageReason}>
          {VendorInvoiceIntakeStageLabels[row.stage]}
        </Chip>
        {row.reviewClaimedByEmail ? (
          <Chip
            tone={row.reviewClaimedByMe ? 'info' : 'warn'}
            title={`Claimed until ${formatDateTime(row.reviewClaimExpiresUtc)}`}
          >
            {row.reviewClaimedByMe ? 'You have this' : row.reviewClaimedByEmail}
          </Chip>
        ) : null}
      </td>
      <td>
        <span className="cell-strong">{row.vendorName ?? '—'}</span>
        <span className="cell-sub">
          <IntakeChip channel={row.channel} />
        </span>
      </td>
      <td>
        {row.invoiceNumber ?? '—'}
        <span className="cell-sub">{formatDate(row.invoiceDate)}</span>
      </td>
      <td className="num">{formatMoney(row.total, row.currencyCode)}</td>
      <td>
        {row.linesAwaitingReview > 0 ? (
          <Chip tone="warn">
            {row.linesAwaitingReview} of {row.lineCount} line(s) to review
          </Chip>
        ) : (
          <Chip tone="good">Lines reviewed</Chip>
        )}
        {row.headerReviewed ? null : <Chip tone="warn">Header not confirmed</Chip>}
        {row.confidence !== null ? <ConfidenceBadge value={row.confidence} /> : null}
      </td>
      <td>
        <Chip tone={TaxReviewStatusTones[row.taxReviewStatus]}>
          {TaxReviewStatusLabels[row.taxReviewStatus]}
        </Chip>
      </td>
      <td>{formatRelative(row.createdUtc)}</td>
    </SelectableRow>
  );
}

// ---------------------------------------------------------------------------
// The review surface
// ---------------------------------------------------------------------------

function IntakeDetail({
  detail,
  taxCodes,
  terms,
  termsLoading,
  accounts,
  accountsLoading,
  canReview,
  onChanged,
  onClose,
  onPrev,
  onNext,
  showPreview,
  onTogglePreview,
}: {
  detail: VendorInvoiceIntakeDetailDto;
  taxCodes: QboTaxCodeDto[];
  terms: QboTermDto[];
  termsLoading: boolean;
  accounts: LocalAccountDto[];
  accountsLoading: boolean;
  canReview: boolean;
  onChanged: () => void;
  onClose: () => void;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
  showPreview: boolean;
  onTogglePreview: () => void;
}): JSX.Element {
  const toast = useToast();
  const summary = detail.summary;
  const [busy, setBusy] = useState(false);
  const [staleWarning, setStaleWarning] = useState<string | null>(null);

  // A refused save is almost always a stale one, and the message the API sends says exactly what
  // happened. Surfacing it here rather than as a toast keeps it beside the figures it is about.
  const run = useCallback(
    async (action: () => Promise<unknown>, success: string) => {
      setBusy(true);
      setStaleWarning(null);
      try {
        await action();
        toast.success(success);
        onChanged();
      } catch (cause) {
        if (
          cause instanceof ApiError
          && (cause.code === 'StaleRevision'
            || cause.code === 'ReviewClaimed'
            || cause.code === 'EditInFlight')
        ) {
          // Three different situations needing three different actions from the reader, so the
          // API's own wording is shown rather than one generic "could not save".
          setStaleWarning(cause.message);
        } else {
          toast.error(errorMessage(cause));
        }
      } finally {
        setBusy(false);
      }
    },
    [onChanged, toast],
  );

  const claimedByOther =
    summary.reviewClaimedByEmail !== null && !summary.reviewClaimedByMe;

  return (
    <>
      <div className="subpanel-toolbar">
        <div className="subpanel-toolbar-nav">
          <button type="button" className="btn btn-ghost btn-sm" disabled={!onPrev} onClick={() => onPrev?.()}>
            ← Previous
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={!onNext} onClick={() => onNext?.()}>
            Next →
          </button>
        </div>
        <div className="subpanel-toolbar-actions">
          <button
            type="button"
            className={showPreview ? 'btn btn-secondary btn-sm' : 'btn btn-ghost btn-sm'}
            onClick={onTogglePreview}
          >
            {showPreview ? 'Hide preview' : 'Preview document'}
          </button>
          <Link className="btn btn-ghost btn-sm" to={`/purchase-invoices?document=${summary.documentId}`}>
            Open to post
          </Link>
          {detail.vendorIntakeResumeScenario !== null ? (
            <Link className="btn btn-ghost btn-sm" to="/vendor-intake">
              Set the supplier up
            </Link>
          ) : null}
          {canReview ? (
            summary.reviewClaimedByMe ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => run(() => documents.release(summary.documentId), 'Released.')}
              >
                Release
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy}
                onClick={() => run(() => documents.claim(summary.documentId), 'Claimed for review.')}
              >
                {claimedByOther ? 'Take the review' : 'Claim for review'}
              </button>
            )
          ) : null}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <InlineError message={staleWarning} />

      <div className={showPreview ? 'drawer-split' : undefined}>
        {showPreview ? (
          <div className="drawer-split-preview">
            <DocumentPreview documentId={summary.documentId} fileName={summary.fileName} />
          </div>
        ) : null}

        <div className="drawer-split-main">
          {claimedByOther ? (
            <p className="notice notice-warn">
              {summary.reviewClaimedByEmail} claimed this for review until{' '}
              {formatDateTime(summary.reviewClaimExpiresUtc)}. Editing it now would overwrite what
              they are working on.
            </p>
          ) : null}

          {detail.blockers.length > 0 ? (
            <div className="notice notice-bad">
              <p className="notice-title">This invoice cannot post yet</p>
              <ul className="notice-list">
                {detail.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <KeyValueList columns={3}>
            <KeyValue label="Invoice number">{summary.invoiceNumber ?? '—'}</KeyValue>
            <KeyValue label="Invoice date">{formatDate(summary.invoiceDate)}</KeyValue>
            <KeyValue label="Due">{formatDate(summary.dueDate)}</KeyValue>
            <KeyValue label="Total">{formatMoney(summary.total, summary.currencyCode)}</KeyValue>
            <KeyValue label="Revision">{summary.revision}</KeyValue>
            <KeyValue label="Uploaded by">{summary.uploadedByEmail ?? '—'}</KeyValue>
            <KeyValue label="Payment terms on the document">
              {detail.extractedPaymentTermsText ?? '—'}
            </KeyValue>
            <KeyValue label="QuickBooks terms">
              {detail.qboTermsName ?? (detail.paymentTermsMappingSkipped ? 'Deliberately unmapped' : '—')}
            </KeyValue>
            <KeyValue label="Source file">
              {detail.documentDownloadUrl ? (
                <Link className="cell-link" to={`/purchase-invoices?document=${summary.documentId}`}>
                  {summary.fileName}
                </Link>
              ) : (
                summary.fileName
              )}
            </KeyValue>
          </KeyValueList>

          {detail.paymentReviewRequired ? (
            <p className="notice notice-warn">
              {detail.paymentReviewReason ?? 'How this is paid or posted needs a person to look.'}
            </p>
          ) : null}

          {summary.qboEntityId ? (
            <p className="notice notice-good">
              Posted to QuickBooks as {summary.qboDocNumber ?? summary.qboEntityId}.{' '}
              {summary.sourceDocumentAttached
                ? `The original invoice is attached (${detail.qboAttachableId}).`
                : detail.attachmentMessage ?? 'The original invoice is not attached.'}
            </p>
          ) : null}

          <HeaderReview
            detail={detail}
            terms={terms}
            termsLoading={termsLoading}
            canReview={canReview && !claimedByOther}
            busy={busy}
            onRun={run}
          />

          <TaxReview detail={detail} canReview={canReview && !claimedByOther} busy={busy} onRun={run} />

          <LineReview
            detail={detail}
            taxCodes={taxCodes}
            accounts={accounts}
            accountsLoading={accountsLoading}
            canReview={canReview && !claimedByOther}
            busy={busy}
            onRun={run}
          />
        </div>
      </div>
    </>
  );
}

type RunFn = (action: () => Promise<unknown>, success: string) => Promise<void>;

function HeaderReview({
  detail,
  terms,
  termsLoading,
  canReview,
  busy,
  onRun,
}: {
  detail: VendorInvoiceIntakeDetailDto;
  terms: QboTermDto[];
  termsLoading: boolean;
  canReview: boolean;
  busy: boolean;
  onRun: RunFn;
}): JSX.Element {
  const summary = detail.summary;
  const [termsId, setTermsId] = useState(detail.qboTermsId ?? '');
  const [termsName, setTermsName] = useState(detail.qboTermsName ?? '');
  const [skipped, setSkipped] = useState(detail.paymentTermsMappingSkipped);

  useEffect(() => {
    setTermsId(detail.qboTermsId ?? '');
    setTermsName(detail.qboTermsName ?? '');
    setSkipped(detail.paymentTermsMappingSkipped);
  }, [detail.qboTermsId, detail.qboTermsName, detail.paymentTermsMappingSkipped]);

  return (
    <section className="subpanel">
      <header className="subpanel-head">
        <h3 className="subpanel-title">Header</h3>
        {summary.headerReviewed ? (
          <Chip tone="good">Confirmed</Chip>
        ) : (
          <Chip tone="warn">Not confirmed</Chip>
        )}
      </header>

      <p className="subpanel-hint">
        Confirming the header means the supplier, invoice number, dates, currency and total are
        right. It is separate from the lines because a right header with wrong lines, and the
        reverse, are both common.
      </p>

      <Toolbar>
        <TermLookupField
          terms={terms}
          loading={termsLoading}
          selectedId={termsId || null}
          selectedName={termsName || null}
          disabled={!canReview}
          onChange={(id, name) => {
            setTermsId(id ?? '');
            setTermsName(name ?? '');
          }}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={skipped}
            disabled={!canReview}
            onChange={(event) => setSkipped(event.target.checked)}
          />
          <span>Deliberately leaving the terms unmapped</span>
        </label>
      </Toolbar>

      {canReview ? (
        <div className="row-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy}
            onClick={() =>
              onRun(
                () =>
                  vendorInvoiceIntake.reviewHeader(summary.documentId, {
                    reviewed: true,
                    qboTermsId: termsId.trim() || null,
                    qboTermsName: termsName.trim() || null,
                    paymentTermsMappingSkipped: skipped,
                    expectedRevision: summary.revision,
                  }),
                'Header confirmed.',
              )
            }
          >
            Confirm the header
          </button>
          {summary.headerReviewed ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() =>
                onRun(
                  () =>
                    vendorInvoiceIntake.reviewHeader(summary.documentId, {
                      reviewed: false,
                      qboTermsId: termsId.trim() || null,
                      qboTermsName: termsName.trim() || null,
                      paymentTermsMappingSkipped: skipped,
                      expectedRevision: summary.revision,
                    }),
                  'Confirmation withdrawn.',
                )
              }
            >
              Withdraw the confirmation
            </button>
          ) : null}
        </div>
      ) : null}

      {summary.headerReviewed && detail.summary.revision ? (
        <p className="subpanel-hint">
          A confirmation is withdrawn automatically when the header changes: a confirmation of
          figures that have since moved is not a confirmation of anything.
        </p>
      ) : null}
    </section>
  );
}

function TaxReview({
  detail,
  canReview,
  busy,
  onRun,
}: {
  detail: VendorInvoiceIntakeDetailDto;
  canReview: boolean;
  busy: boolean;
  onRun: RunFn;
}): JSX.Element {
  const tax = detail.tax;
  const [note, setNote] = useState('');

  return (
    <section className="subpanel">
      <header className="subpanel-head">
        <h3 className="subpanel-title">Tax</h3>
        <Chip tone={TaxReviewStatusTones[tax.status]}>{TaxReviewStatusLabels[tax.status]}</Chip>
        {tax.automatedSalesTax ? (
          <Chip tone="info" title="QuickBooks recomputes sales tax itself in this company.">
            QuickBooks computes tax
          </Chip>
        ) : null}
      </header>

      {tax.actionMessage ? <p className="notice notice-info">{tax.actionMessage}</p> : null}
      {tax.blocker ? <p className="notice notice-bad">{tax.blocker}</p> : null}

      <KeyValueList columns={3}>
        <KeyValue label="Stated on the invoice">
          {formatMoney(tax.sourceTaxAmount, detail.summary.currencyCode)}
        </KeyValue>
        <KeyValue label="This company's codes give">
          {formatMoney(tax.calculatedTaxAmount, detail.summary.currencyCode)}
        </KeyValue>
        <KeyValue label="Difference">
          {tax.taxDifference === null
            ? '—'
            : `${formatMoney(tax.taxDifference, detail.summary.currencyCode)} (tolerance ${formatMoney(
                tax.tolerance,
                detail.summary.currencyCode,
              )})`}
        </KeyValue>
        <KeyValue label="Rate read from the document">
          {tax.sourceTaxPercent === null ? '—' : `${tax.sourceTaxPercent}%`}
        </KeyValue>
        <KeyValue label="Taxable base">
          {formatMoney(tax.sourceTaxableBase, detail.summary.currencyCode)}
        </KeyValue>
        <KeyValue label="Last checked">{formatDateTime(tax.lastCalculatedUtc)}</KeyValue>
        <KeyValue label="Evidence" wide>
          {tax.sourceTaxEvidence ?? '—'}
          {tax.sourceTaxEvidenceSource ? ` (from ${tax.sourceTaxEvidenceSource})` : ''}
        </KeyValue>
        {tax.reviewedByEmail ? (
          <KeyValue label="Difference accepted by" wide>
            {tax.reviewedByEmail} on {formatDateTime(tax.reviewedUtc)} — {tax.reviewNote}
          </KeyValue>
        ) : null}
      </KeyValueList>

      {canReview ? (
        <>
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() =>
                onRun(
                  () =>
                    vendorInvoiceIntake.recalculateTax(
                      detail.summary.documentId, detail.summary.revision),
                  'Tax rechecked.',
                )
              }
            >
              Recheck the tax
            </button>
          </div>

          {tax.status === TaxReviewStatus.Differs
          || tax.status === TaxReviewStatus.UncomputableTax ? (
            <>
              <TextAreaField
                label={
                  tax.status === TaxReviewStatus.UncomputableTax
                    ? 'You checked the tax against the document yourself. What did you find?'
                    : 'Why is the difference acceptable?'
                }
                value={note}
                onChange={setNote}
                rows={2}
                placeholder={
                  tax.status === TaxReviewStatus.UncomputableTax
                    ? "e.g. checked the 12.00 by hand against the invoice; compound rate is correct"
                    : 'e.g. the supplier billed at the old rate; agreed with their AP team'
                }
              />
              <div className="row-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy || note.trim().length === 0}
                  onClick={() =>
                    onRun(
                      () =>
                        vendorInvoiceIntake.acceptTaxDifference(detail.summary.documentId, {
                          note: note.trim(),
                          expectedRevision: detail.summary.revision,
                        }),
                      'Difference accepted.',
                    )
                  }
                >
                  {tax.status === TaxReviewStatus.UncomputableTax
                    ? 'Accept the tax as stated'
                    : 'Accept the difference'}
                </button>
              </div>
              <p className="subpanel-hint">
                {tax.status === TaxReviewStatus.UncomputableTax
                  ? 'This company\u2019s codes cannot produce a figure for at least one line, so nothing '
                    + 'has been verified. Accepting records that you checked it yourself, with what you '
                    + 'found. Rechecking the tax withdraws the acceptance.'
                  : 'A reason is required. An accepted tax difference with no explanation is not '
                    + 'evidence of a decision, and rechecking the tax withdraws the acceptance because '
                    + 'it is then about figures the invoice no longer carries.'}
              </p>
            </>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** One row's working state — the server's line plus whatever a person has typed but not saved yet. */
interface LineDraft {
  decision: IntakeLineReviewState;
  description: string;
  quantity: string;
  unitCost: string;
  amount: string;
  codingType: 'account' | 'item';
  accountId: string;
  accountName: string | null;
  itemId: string;
  taxCodeId: string;
  note: string;
}

function draftFromLine(line: VendorInvoiceIntakeLineDto): LineDraft {
  return {
    decision: line.reviewState,
    description: line.description ?? '',
    quantity: line.quantity === null ? '' : String(line.quantity),
    unitCost: line.unitCost === null ? '' : String(line.unitCost),
    amount: line.lineAmount === null ? '' : String(line.lineAmount),
    // An item-coded line and an account-coded line are two different things a line can BE, not two
    // fields that both happen to be filled in. Item wins on load only because that is what the
    // stored line already is.
    codingType: line.qboItemId ? 'item' : 'account',
    accountId: line.qboAccountId ?? '',
    accountName: line.qboAccountName ?? null,
    itemId: line.qboItemId ?? '',
    taxCodeId: line.qboTaxCodeId ?? '',
    note: line.reviewNote ?? '',
  };
}

function draftsFromLines(lines: VendorInvoiceIntakeLineDto[]): Record<number, LineDraft> {
  return Object.fromEntries(lines.map((line) => [line.lineNo, draftFromLine(line)]));
}

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The WHOLE line, every time. A line override replaces the stored override for that line number
 * rather than patching it, so sending a subset would clear whatever was left out.
 */
function draftToEntry(lineNo: number, draft: LineDraft): IntakeLineBatchEntry {
  return {
    lineNo,
    decision: draft.decision,
    description: draft.description.trim() || null,
    quantity: numberOrNull(draft.quantity),
    unitCost: numberOrNull(draft.unitCost),
    lineAmount: numberOrNull(draft.amount),
    qboAccountId: draft.codingType === 'account' ? draft.accountId.trim() || null : null,
    qboItemId: draft.codingType === 'item' ? draft.itemId.trim() || null : null,
    qboTaxCodeId: draft.taxCodeId.trim() || null,
    reviewNote: draft.note.trim() || null,
  };
}

function LineReview({
  detail,
  taxCodes,
  accounts,
  accountsLoading,
  canReview,
  busy,
  onRun,
}: {
  detail: VendorInvoiceIntakeDetailDto;
  taxCodes: QboTaxCodeDto[];
  accounts: LocalAccountDto[];
  accountsLoading: boolean;
  canReview: boolean;
  busy: boolean;
  onRun: RunFn;
}): JSX.Element {
  const [drafts, setDrafts] = useState<Record<number, LineDraft>>(() => draftsFromLines(detail.lines));
  const [touched, setTouched] = useState<Set<number>>(new Set());

  // Rebuilt whenever the document changes or the projection is refreshed — a save's own refresh
  // becomes the new baseline, and switching documents (Previous/Next) never carries an unsaved
  // draft from one invoice's lines onto another's.
  useEffect(() => {
    setDrafts(draftsFromLines(detail.lines));
    setTouched(new Set());
  }, [detail.summary.documentId, detail.lines]);

  const updateDraft = (lineNo: number, patch: Partial<LineDraft>) => {
    setDrafts((prev) => {
      const current = prev[lineNo];
      if (!current) return prev;
      // Any edit is itself a decision to correct the line, unless it was explicitly excluded —
      // excluding stands until the reviewer confirms or corrects it again.
      const decision =
        patch.decision
        ?? (current.decision === IntakeLineReviewState.Excluded
          ? current.decision
          : IntakeLineReviewState.Corrected);
      return { ...prev, [lineNo]: { ...current, ...patch, decision } };
    });
    setTouched((prev) => new Set(prev).add(lineNo));
  };

  const revertLine = (lineNo: number) => {
    const line = detail.lines.find((l) => l.lineNo === lineNo);
    if (!line) return;
    setDrafts((prev) => ({ ...prev, [lineNo]: draftFromLine(line) }));
    setTouched((prev) => {
      const next = new Set(prev);
      next.delete(lineNo);
      return next;
    });
  };

  const discardAll = () => {
    setDrafts(draftsFromLines(detail.lines));
    setTouched(new Set());
  };

  const pendingCount = touched.size;

  const saveAll = () =>
    onRun(
      () =>
        vendorInvoiceIntake.reviewLinesBatch(detail.summary.documentId, {
          lines: Array.from(touched)
            .filter((lineNo) => drafts[lineNo])
            .map((lineNo) => draftToEntry(lineNo, drafts[lineNo])),
          expectedRevision: detail.summary.revision,
        }),
      pendingCount === 1 ? '1 line saved.' : `${pendingCount} lines saved.`,
    );

  const awaiting = useMemo(
    () => detail.lines.filter((line) => line.reviewState === IntakeLineReviewState.NotReviewed).length,
    [detail.lines],
  );

  return (
    <section className="subpanel">
      <header className="subpanel-head">
        <h3 className="subpanel-title">Lines</h3>
        {awaiting > 0 ? (
          <Chip tone="warn">{awaiting} awaiting review</Chip>
        ) : (
          <Chip tone="good">All reviewed</Chip>
        )}
        {pendingCount > 0 ? <Chip tone="warn">{pendingCount} unsaved</Chip> : null}
        {canReview ? (
          <>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() =>
                onRun(
                  () => vendorInvoiceIntake.recommend(detail.summary.documentId),
                  "Suggestions read from this company's own posting history.",
                )
              }
            >
              Suggest accounts
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy || pendingCount === 0}
              onClick={discardAll}
            >
              Discard changes
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || pendingCount === 0}
              onClick={saveAll}
            >
              {pendingCount === 0 ? 'Save changes' : `Save ${pendingCount} change${pendingCount === 1 ? '' : 's'}`}
            </button>
          </>
        ) : null}
      </header>

      <TableWrap busy={busy}>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Description</th>
            <th scope="col" className="num">Qty</th>
            <th scope="col" className="num">Unit</th>
            <th scope="col" className="num">Amount</th>
            <th scope="col">Coding</th>
            <th scope="col">Tax code</th>
            <th scope="col">Review</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {detail.lines.map((line) => {
            const draft = drafts[line.lineNo] ?? draftFromLine(line);
            return (
              <LineRow
                key={line.lineNo}
                line={line}
                draft={draft}
                dirty={touched.has(line.lineNo)}
                taxCodes={taxCodes}
                accounts={accounts}
                accountsLoading={accountsLoading}
                canReview={canReview}
                busy={busy}
                currencyCode={detail.summary.currencyCode}
                onChange={(patch) => updateDraft(line.lineNo, patch)}
                onConfirm={() => updateDraft(line.lineNo, { decision: IntakeLineReviewState.Confirmed })}
                onExclude={() => updateDraft(line.lineNo, { decision: IntakeLineReviewState.Excluded })}
                onRevert={() => revertLine(line.lineNo)}
              />
            );
          })}
        </tbody>
      </TableWrap>

      <p className="subpanel-hint">
        Every line is editable at once — correct as many as you need, then save them together. Saving
        goes through the same edit path as the invoice review pane, so the whole batch bumps the
        revision once, invalidates any approval in flight and sends the invoice back through approval
        when one is required.
      </p>
    </section>
  );
}

function LineRow({
  line,
  draft,
  dirty,
  taxCodes,
  accounts,
  accountsLoading,
  canReview,
  busy,
  currencyCode,
  onChange,
  onConfirm,
  onExclude,
  onRevert,
}: {
  line: VendorInvoiceIntakeLineDto;
  draft: LineDraft;
  dirty: boolean;
  taxCodes: QboTaxCodeDto[];
  accounts: LocalAccountDto[];
  accountsLoading: boolean;
  canReview: boolean;
  busy: boolean;
  currencyCode: string | null;
  onChange: (patch: Partial<LineDraft>) => void;
  onConfirm: () => void;
  onExclude: () => void;
  onRevert: () => void;
}): JSX.Element {
  return (
    <tr className={draft.decision === IntakeLineReviewState.Excluded ? 'row-muted' : undefined}>
      <td>
        {line.lineNo}
        {line.lowConfidence ? (
          <span className="cell-sub">
            <Chip tone="warn">Low confidence</Chip>
            {line.confidence !== null ? <ConfidenceBadge value={line.confidence} /> : null}
          </span>
        ) : null}
      </td>
      <td>
        <input
          className="input input-inline"
          aria-label={`Line ${line.lineNo} description`}
          value={draft.description}
          disabled={!canReview}
          onChange={(event) => onChange({ description: event.target.value })}
        />
      </td>
      <td className="num">
        <input
          className="input input-inline num"
          aria-label={`Line ${line.lineNo} quantity`}
          value={draft.quantity}
          disabled={!canReview}
          inputMode="decimal"
          onChange={(event) => onChange({ quantity: event.target.value })}
        />
      </td>
      <td className="num">
        <input
          className="input input-inline num"
          aria-label={`Line ${line.lineNo} unit cost`}
          value={draft.unitCost}
          disabled={!canReview}
          inputMode="decimal"
          onChange={(event) => onChange({ unitCost: event.target.value })}
        />
      </td>
      <td className="num">
        <input
          className="input input-inline num"
          aria-label={`Line ${line.lineNo} amount`}
          value={draft.amount}
          disabled={!canReview}
          inputMode="decimal"
          onChange={(event) => onChange({ amount: event.target.value })}
        />
        {!dirty ? (
          <span className="cell-sub">{formatMoney(line.lineAmount, currencyCode)}</span>
        ) : null}
      </td>
      <td>
        <Chip tone="neutral">{IntakeLineTypeLabels[line.lineType]}</Chip>
        <select
          className="input select input-inline"
          aria-label={`Line ${line.lineNo} coding type`}
          value={draft.codingType}
          disabled={!canReview}
          onChange={(event) => {
            const next = event.target.value === 'item' ? 'item' : 'account';
            // Switching clears whichever field no longer applies, so a stale value from the other
            // coding type can never ride along on the save.
            onChange(
              next === 'item'
                ? { codingType: next, accountId: '', accountName: null }
                : { codingType: next, itemId: '' },
            );
          }}
        >
          <option value="account">Account (incl. Fixed Asset)</option>
          <option value="item">Item</option>
        </select>
        {draft.codingType === 'account' ? (
          <AccountLookupField
            label="QuickBooks account"
            searchLabel="Find account"
            role="expense"
            accounts={accounts}
            loading={accountsLoading}
            selectedId={draft.accountId || null}
            selectedName={draft.accountName}
            disabled={!canReview}
            onChange={(id, name) => onChange({ accountId: id ?? '', accountName: name })}
          />
        ) : (
          <input
            className="input input-inline"
            aria-label={`Line ${line.lineNo} item id`}
            value={draft.itemId}
            disabled={!canReview}
            placeholder="No item cache yet — typed by hand"
            onChange={(event) => onChange({ itemId: event.target.value })}
          />
        )}
        {line.recommendationStatus !== LineRecommendationStatus.None ? (
          <span className="cell-sub" title={line.recommendationBasis ?? undefined}>
            {LineRecommendationStatusLabels[line.recommendationStatus]}
            {line.recommendedAccountName ? `: ${line.recommendedAccountName}` : ''}
          </span>
        ) : null}
      </td>
      <td>
        <select
          className="input select input-inline"
          aria-label={`Line ${line.lineNo} tax code`}
          value={draft.taxCodeId}
          disabled={!canReview}
          onChange={(event) => onChange({ taxCodeId: event.target.value })}
        >
          <option value="">No tax code</option>
          {taxCodes.map((code) => (
            <option key={code.id} value={code.id}>
              {code.purchaseRatePercent === null
                ? code.name
                : `${code.name} (${code.purchaseRatePercent}%)`}
            </option>
          ))}
        </select>
        {line.taxResolution === LineTaxResolution.CodeNotInCompany
        || line.taxResolution === LineTaxResolution.RateNotComputable ? (
          <span className="cell-sub">
            <Chip tone={LineTaxResolutionTones[line.taxResolution]}>
              {LineTaxResolutionLabels[line.taxResolution]}
            </Chip>
          </span>
        ) : null}
        {line.extractedTaxCodeText ? (
          <span className="cell-sub" title="Text read off the invoice. Never sent to QuickBooks.">
            document says &ldquo;{line.extractedTaxCodeText}&rdquo;
          </span>
        ) : null}
      </td>
      <td>
        {canReview ? (
          <div className="row-actions">
            <button
              type="button"
              className={
                draft.decision === IntakeLineReviewState.Confirmed
                  ? 'btn btn-secondary btn-sm'
                  : 'btn btn-ghost btn-sm'
              }
              disabled={busy}
              onClick={onConfirm}
            >
              Confirm
            </button>
            <button
              type="button"
              className={
                draft.decision === IntakeLineReviewState.Excluded
                  ? 'btn btn-secondary btn-sm'
                  : 'btn btn-ghost btn-sm'
              }
              disabled={busy}
              onClick={onExclude}
            >
              Exclude
            </button>
          </div>
        ) : null}
        {dirty ? (
          <input
            className="input input-inline"
            aria-label={`Line ${line.lineNo} review note`}
            value={draft.note}
            placeholder="Note (optional)"
            disabled={!canReview}
            onChange={(event) => onChange({ note: event.target.value })}
          />
        ) : (
          <>
            <Chip tone={IntakeLineReviewStateTones[line.reviewState]}>
              {IntakeLineReviewStateLabels[line.reviewState]}
            </Chip>
            {line.reviewedByEmail ? (
              <span className="cell-sub">
                {line.reviewedByEmail}, {formatRelative(line.reviewedUtc)}
              </span>
            ) : null}
          </>
        )}
      </td>
      <td className="row-actions">
        {dirty ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onRevert}>
            Revert
          </button>
        ) : null}
      </td>
    </tr>
  );
}
